import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

/** Signed-in app and admin surfaces where the marketing sticky header
    would be noise. Everywhere else it appears on scroll. */
const APP_ROUTE_PREFIXES = ["/dashboard", "/intake", "/analysis", "/admin", "/briefings", "/diagnostic"];

/**
 * Slim sticky header, sitewide (mobile and desktop). Slides in from the top
 * once the main header scrolls out of view, so it never covers content at
 * rest and stays unobtrusive. Contents per spec: the CLARITY wordmark on the
 * left, one CTA on the right that links directly to the four-question
 * assessment (/assessment auto-opens the quiz).
 */
export default function StickyMiniNav({ threshold = 300 }) {
  const { pathname, search } = useLocation();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const onScroll = () => setShown(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  if (APP_ROUTE_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  /* Landing pages with an in-page assessment modal: the header CTA opens the
     same modal the page's own CTAs use (via a window event the page listens
     for) instead of routing to /assessment, so the reader never leaves the
     page's funnel. The conversion event is untouched: it fires from the quiz
     itself at completion + email submit, regardless of how it was opened. */
  const LOCAL_QUIZ = {
    "/mugenvip": {
      label: "Map your exposure",
      event: "w3rc:mugen-map-exposure",
      onTrack: () => track("mugenvip_cta_click", { cta: "sticky_map_exposure" }),
    },
    "/emtechvip": {
      label: "Request access",
      event: "w3rc:emtech-request-access",
      onTrack: () => track("emtechvip_cta_click", { cta: "sticky_request_access" }),
    },
    "/start": {
      label: "Start the assessment",
      event: "w3rc:start-open-quiz",
      onTrack: () => track("start_cta_click", { cta: "sticky_start_assessment" }),
    },
  }[pathname];
  const ctaClass =
    "inline-flex items-center gap-2 bg-blueprint text-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border border-blueprint whitespace-nowrap hover:bg-ink hover:border-ink transition-colors shrink-0";

  return (
    <div
      data-testid="sticky-mini-nav"
      aria-hidden={!shown}
      className={`fixed top-0 left-0 right-0 z-40 bg-white border-b border-ink transition-transform duration-300 ease-out print:hidden ${
        shown ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-12 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="sticky-brand"
          tabIndex={shown ? 0 : -1}
          className="flex items-baseline gap-2 min-w-0"
        >
          <span className="font-serif text-base sm:text-lg font-semibold tracking-tighter leading-none whitespace-nowrap truncate">
            Web3 Ready CLARITY<sup className="text-[0.45em] align-super ml-0.5">™</sup>
          </span>
        </Link>
        {LOCAL_QUIZ ? (
          <button
            type="button"
            data-testid="sticky-cta-assessment"
            tabIndex={shown ? 0 : -1}
            onClick={() => {
              LOCAL_QUIZ.onTrack();
              window.dispatchEvent(new CustomEvent(LOCAL_QUIZ.event));
            }}
            className={ctaClass}
          >
            {LOCAL_QUIZ.label}
            <ArrowRight size={13} className="hidden sm:block" />
          </button>
        ) : (
          <Link
            to={`/assessment${search || ""}`}
            data-testid="sticky-cta-assessment"
            tabIndex={shown ? 0 : -1}
            onClick={() => track("quiz_open", { source: "sticky_header" })}
            className={ctaClass}
          >
            Start the assessment
            <ArrowRight size={13} className="hidden sm:block" />
          </Link>
        )}
      </div>
    </div>
  );
}
